import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";
import SectionGlow from "./SectionGlow";
import { ease, duration, stagger as staggerToken } from "@/lib/motion";

const points = [
  "Custom-built. No templates, no shortcuts.",
  "Built for roofers, HVAC, landscapers, and remodelers.",
  "7-day turnaround. You work directly with me.",
  "Built to turn visitors into paying customers.",
];

const statCards = [
  { num: "SEO", label: "Optimized" },
  { num: "Free", label: "Revisions" },
  { num: "100%", label: "Custom Design" },
  { num: "7 day", label: "Avg. Turnaround" },
];

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: staggerToken } },
};

const item = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: duration.smooth, ease: ease.smooth } },
};

const mobileItem = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.smooth, ease: ease.smooth } },
};

function parseStat(num: string): { target: number; suffix: string; decimals: number; numeric: boolean } {
  const match = num.match(/^(\d+(?:\.\d+)?)(.*)/);
  if (!match) return { target: 0, suffix: num, decimals: 0, numeric: false };
  const numStr = match[1];
  const suffix = match[2];
  const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
  return { target: parseFloat(numStr), suffix, decimals, numeric: true };
}

function useCountUp(target: number, decimals: number, inView: boolean, skip: boolean) {
  const [value, setValue] = useState(skip ? target : 0);

  useEffect(() => {
    if (skip) { setValue(target); return; }
    if (!inView) return;
    const duration = 1200;
    const start = performance.now();
    let rafId: number;
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [inView, target, decimals, skip]);

  return value;
}

function StatDisplay({ num, inView, skip }: { num: string; inView: boolean; skip: boolean }) {
  const { target, suffix, decimals, numeric } = parseStat(num);
  const value = useCountUp(target, decimals, inView, skip || !numeric);
  if (!numeric) return <>{num}</>;
  const display = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
  return <>{display}{suffix}</>;
}

const AboutSection = () => {
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const activeItem = isMobile ? mobileItem : item;
  const [statsInView, setStatsInView] = useState(false);
  const skip = prefersReducedMotion ?? false;

  return (
    <section
      id="about"
      className="lg:sticky top-0 min-h-0 lg:min-h-[100dvh] flex items-center bg-section-warm [overflow:clip] relative"
      style={{ zIndex: 2 }}
      data-dark-section
    >
      <SectionGlow placement="top-left" />

      <div className="container mx-auto py-16 md:py-20 lg:py-0">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 md:gap-20 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            <motion.p variants={activeItem} className="text-xs font-semibold uppercase tracking-[0.25em] text-sage mb-3 md:text-sm">
              Why Greenleaf
            </motion.p>
            <motion.h2 variants={activeItem} className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] text-cream mb-6 leading-[1.1] font-bold">
              We build websites that actually{" "}
              <span className="text-accent-sweep">work</span> for your business.
            </motion.h2>
            <motion.p variants={activeItem} className="text-cream/80 leading-relaxed mb-8 md:text-lg max-w-lg">
              Based in Raleigh. We build sites for home service pros that look sharp, rank on Google, and bring in real customers.
            </motion.p>
            <ul className="relative z-10 space-y-3">
              {points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <CheckCircle className="h-4 w-4 text-sage mt-1 shrink-0 md:h-5 md:w-5" />
                  <span className="text-cream text-sm md:text-base">{point}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {isMobile ? (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              onViewportEnter={() => setStatsInView(true)}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: duration.smooth, ease: ease.smooth }}
              className="grid grid-cols-2 gap-3"
            >
              {statCards.map((stat) => (
                <div key={stat.label} className="bg-cream/[0.02] border border-sage/20 rounded-xl p-6">
                  <p className="font-heading text-2xl sm:text-3xl text-sage-bright mb-1">
                    <StatDisplay num={stat.num} inView={statsInView} skip={skip} />
                  </p>
                  <p className="text-[10px] sm:text-xs text-cream/70 font-medium uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              onViewportEnter={() => setStatsInView(true)}
              viewport={{ once: true, amount: 0.1 }}
              className="grid grid-cols-2 gap-3 md:gap-4"
            >
              {statCards.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: duration.smooth, delay: i * staggerToken, ease: ease.smooth }}
                  className="bg-cream/[0.02] border border-sage/20 rounded-xl p-6 md:p-8 group hover:bg-cream/[0.04] hover:border-sage/40 transition-[background-color,border-color,box-shadow] duration-300 hover:shadow-md hover:shadow-black/20"
                >
                  <p className="font-heading text-2xl sm:text-3xl md:text-4xl text-sage-bright mb-1">
                    <StatDisplay num={stat.num} inView={statsInView} skip={skip} />
                  </p>
                  <p className="text-[10px] sm:text-xs text-cream/70 font-medium uppercase tracking-wider">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
